import { FeatureCollection } from "geojson";
import { CustomSource } from "../models/CustomSource";
import { mapActive } from "../utils/map-utils";
import turf from "../../shared/utils/turf";
import { RootStore } from "../../shared/stores/RootStore";

export class PendingCoordChangesMapSource {
  map: mapboxgl.Map;

  rootStore: RootStore;
  static sourceName = CustomSource.PendingCoordChanges;

  constructor(map: mapboxgl.Map, rootStore: RootStore) {
    this.map = map;
    this.rootStore = rootStore;

    if (mapActive(this.map) && !this.map.getSource(sourceName)) {
      this.map.addSource(sourceName, sourceConfig);
      this.map.addLayer(lineLayerConfig);
    }
  }

  clear() {
    if (mapActive(this.map)) {
      try {
        this.map.removeLayer(lineLayerConfig.id);
        this.map.removeSource(sourceName);
      } catch (e) {
        console.warn("Error while removing layers and sources", e);
      }
    }
  }

  onStoreUpdate() {
    const { osaNodeStore, osaWayStore } = this.rootStore;
    const selectedOsaWay = osaWayStore.getSelected();

    const nodes = selectedOsaWay
      ? osaNodeStore.nodesByWayId.get(selectedOsaWay.id) ?? []
      : [];

    const features: GeoJSON.Feature[] = [];

    nodes.forEach((node) => {
      const matchedCoords = osaNodeStore.pendingCoordsByNodeId.get(node.id);
      if (matchedCoords) {
        features.push(
          turf.lineString([[node.longitude, node.latitude], matchedCoords]),
        );
      }
    });

    this.setMapData(features);
  }

  private setMapData(features: GeoJSON.Feature[]) {
    if (mapActive(this.map)) {
      const source = this.map.getSource(sourceName) as mapboxgl.GeoJSONSource;
      source?.setData({
        type: "FeatureCollection",
        features,
      });
    }
  }

  reset() {
    this.setMapData([]);
  }
}

const defaultGeoJson: FeatureCollection = {
  type: "FeatureCollection",
  features: [],
};

const sourceName = PendingCoordChangesMapSource.sourceName;
const sourceConfig: mapboxgl.GeoJSONSourceSpecification = {
  type: "geojson",
  promoteId: "id",
  data: defaultGeoJson,
};

const lineLayerName = `${sourceName}-line`;
const lineLayerConfig: mapboxgl.LineLayerSpecification = {
  id: lineLayerName,
  source: sourceName,
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": "#12D3C4",
    "line-width": 3,
    "line-dasharray": [2, 2],
  },
};
